import {
  IBayPattern,
  IRowPattern,
  SmartPairedBayBlock,
  SmartUnpairedBayBlock,
  TIContainerInformationExtendedValid,
} from "@tedivo/tedivo-bay-grid-pure";

import { BayLevelEnum } from "open-vessel-definition";
import { TGetContainersFilterOutput } from "./TGetContainersFilterOutput";
import { baysCoveredBy, topContainersOfStack } from "./topContainersOfStack";

type TBlock = SmartUnpairedBayBlock | SmartPairedBayBlock;

/**
 * The highest container of every stack, seen from above.
 *
 * The smart blocks are split by level, so a bay has an above-deck block and a
 * below-deck block with the same stacks. Taking the top of every stack in every
 * block returned the top of the hold as well as the top of the deck cargo above
 * it, and both were drawn on the same spot. The hold is only visible where the
 * deck above it is empty, so a below-deck block needs the above-deck block(s) of
 * the same bays to know which of its stacks are covered.
 *
 * @param allBlocks every block of the vessel; without it the block is filtered
 * on its own, as before.
 */
export function getContainersByHighestAll(
  smartBay: TBlock,
  allBlocks: TBlock[] = [],
): TGetContainersFilterOutput {
  const containers: TIContainerInformationExtendedValid[] = [];

  const { gridContent, allStacksKeys, allTiersKeys } = smartBay.getGridContent();

  const coveredRows =
    smartBay.level === BayLevelEnum.BELOW ? rowsCoveredAbove(smartBay, allBlocks) : undefined;

  // For each stack, get the highest container (top tier)
  for (const stackKey of allStacksKeys) {
    const topContainers = topContainersOfStack(
      smartBay,
      gridContent,
      stackKey,
      allTiersKeys,
      "highest",
    );

    containers.push(
      ...(coveredRows
        ? topContainers.filter((c) => !isCoveredAbove(c, stackKey, smartBay, coveredRows))
        : topContainers),
    );
  }

  return {
    containers,
    tBaysStr: smartBay.tBaysStr,
    level: smartBay.level,
  };
}

/**
 * For a below-deck block: the rows that hold cargo in the above-deck blocks of
 * the same bays, per odd bay.
 */
function rowsCoveredAbove(
  belowBlock: TBlock,
  allBlocks: TBlock[],
): Map<IBayPattern, Set<IRowPattern>> {
  const covered = new Map<IBayPattern, Set<IRowPattern>>();
  const ownBays = new Set(belowBlock.getEnclosedBays());

  allBlocks
    .filter(
      (block) =>
        block.level === BayLevelEnum.ABOVE &&
        block.getEnclosedBays().some((bay) => ownBays.has(bay)),
    )
    .forEach((aboveBlock) => {
      const { gridContent, allStacksKeys, allTiersKeys } = aboveBlock.getGridContent();

      for (const row of allStacksKeys) {
        for (const tier of allTiersKeys) {
          const content = gridContent[row]?.[tier];
          if (!content) continue;

          (content.containers as TIContainerInformationExtendedValid[]).forEach((c) => {
            baysCoveredBy(c, aboveBlock).forEach((bay) => {
              if (!covered.has(bay)) covered.set(bay, new Set());
              covered.get(bay)?.add(row);
            });
          });
        }
      }
    });

  return covered;
}

/**
 * A hold container is hidden when deck cargo sits over its whole footprint. A 40
 * with only one of its bays covered still shows its other half, so it stays.
 */
function isCoveredAbove(
  c: TIContainerInformationExtendedValid,
  row: IRowPattern,
  block: TBlock,
  coveredRows: Map<IBayPattern, Set<IRowPattern>>,
): boolean {
  const bays = baysCoveredBy(c, block);
  return bays.length > 0 && bays.every((bay) => coveredRows.get(bay)?.has(row));
}
